// src/auth.ts — client-side auth hooks. Reads identity & role exclusively from /api/me.

import { useCallback, useEffect, useState } from "react";

// Phase 9 -- per-app permission grid. Keys mirror the backend
// `AppPermissions` interface in api/src/auth.ts. The shell uses this to
// gate which bottom-nav entries render. Adding a new app means adding the
// key here AND in api/src/auth.ts/DEFAULT_APPS.
//
// Phase 9 follow-up (2026-05-21) -- `headlines` gates the home-page
// Headlines section. Defaults to true server-side via DEFAULT_APPS, so
// existing users see it unchanged.
export interface AppPermissions {
  contacts: boolean;
  weekly: boolean;
  trains: boolean;
  newsletter: boolean;
  // Newsletter Reader (migration 124) -- read side of the uploaded-PDF members'
  // newsletter. On by default (server DEFAULT_APPS) so it reads as universal.
  newsletterReader: boolean;
  tickets: boolean;
  vacancies: boolean;
  docs: boolean;
  firstAider: boolean;
  // "Call to First Aiders" tile grant (Apps page). Off by default; admins grant
  // it per-user. Distinct from FeatureFlags.firstAiderCallout (system kill-switch).
  firstAiderCallout: boolean;
  userAccess: boolean;
  auditLog: boolean;
  nlcreator: boolean;
  articleWriter: boolean;
  alerts: boolean;
  headlines: boolean;
  duty: boolean;
  // Driving Licence Check (migration 074). Gates the DRIVER self-service tile +
  // view; set in the Access Apps grid. The DV Admin view is gated by the
  // top-level isDvCheckAdmin flag instead.
  drivingLicenceCheck: boolean;
  // Long Service Awards (migration 085). Gates the RECIPIENT LS Travel Pass
  // tile + view; set in the Access Apps grid. The LS Admin console is gated by
  // the top-level isLongServiceAdmin flag instead.
  longService: boolean;
  // Issue Manager (CODE-001). Server-side default is being added by chat-Claude
  // in ENG-004; until then this key may be absent from /api/me responses, in
  // which case the `=== false` check in isAppVisible falls open and the tile
  // is shown. Once the server lands, this becomes the real gate.
  issues: boolean;
  // Reports applet (migration 097). `reports` gates the applet tile + the Home
  // pinned-measure slice; `reportsExec` gates the Executive tab inside the app
  // (a sub-permission of reports -- the API + User Access coerce it off whenever
  // reports is off).
  reports: boolean;
  reportsExec: boolean;
  // Training & Competencies (migration 098). Gates the My Training tile + view.
  // Derived server-side: true once the user has a matched iHasco training record.
  training: boolean;
  // Incident Log (migration 101). READ ONLY access — view all H&S incidents.
  // FULL ACCESS (raise/edit/manage) is the top-level isIncidentAdmin flag.
  incidentLog: boolean;
  // Raise an Issue — standalone reporter app split out of Issue Manager. Gates
  // the /raise-issue app (Raise + My Issues) and the raise action server-side.
  // Off for everyone by default; granted per-user in User Access.
  raiseIssue: boolean;
  // BigPlanSTN (migration 104). Grants the user signal-box iPad sign-in for The
  // Big Plan's Special Traffic Notices. When on, the "Signal-box assignment"
  // section appears for them in User Access (which boxes they may pick). Off by
  // default; granted per-user in the Access Apps grid.
  bigPlanStn: boolean;
  // BigPlanDevices (migration 105). The iPad device-registry admin app: approve
  // iPads, mint pairing codes, see who's signed in, disable/delete (revoke).
  // Gates the BigPlanDevices tile + /bigplan-devices page. Admin tool; off by default.
  bigPlanDevices: boolean;
  // Mini-Blog (migration 128). `miniBlog` gates the department-scoped reader
  // tile + stream (on by default — the department filter is the real audience
  // gate). `miniBlogCreator` is the Author grant; off by default.
  miniBlog: boolean;
  miniBlogCreator: boolean;
  // Live Tracker (2026-07-21) — GPS tracker fleet health & live map. Off by
  // default; granted per-user in User Access. Admins always have access.
  liveTracker: boolean;
  // CamView (2026-07-21) — on-demand station CCTV stills. Off by default;
  // granted per-user in User Access. Admins always have access.
  camView: boolean;
  // CamView Pro (2026-08-10) — extended CCTV set (yard/crossing cameras). Its
  // own grant, off by default (NOT force-enabled like camView). Admins always
  // have access. Mirrors api/src/auth.ts AppPermissions.
  camViewPro: boolean;
  // Rostering (2026-07-27) — a thin personal view onto rostering.nymr.co.uk for
  // the signed-in volunteer (my turns, availability, clock-on & compliance).
  // Mirrors api/src/auth.ts AppPermissions. OFF by default for everyone in the
  // server DEFAULT_APPS; migration 147 seeds the grant to chris.flynn@ only, so
  // the tile + route + server proxy are all invisible/inaccessible until it is
  // released per-user in User Access. Tile is ssoOnly (management/SSO applet).
  rostering: boolean;
  // Out Of Office (migration 150) — set the auto-reply on managed shared
  // mailboxes. Off by default; admins always have access.
  outOfOffice: boolean;
}

// Phase 9 c14 -- maintenance state surfaced on /api/me so the shell can
// flip into the holding page without an extra round trip. `defaultBanner`
// is owned by the backend (DEFAULT_MAINTENANCE_BANNER constant) so admins
// can update it server-side without a redeploy.
export interface SystemMaintenanceState {
  active: boolean;
  message: string | null;
  defaultBanner: string;
  updatedAt: string | null;
}

// Sign-In System (Build 1). Mirrors api/src/sign-in.ts. The SPA renders the
// Home duty surface (and Build 2's Group QM) ONLY when signIn.eligible AND
// features.dutySignin. Ineligible users see nothing (silent gating).
export interface SignInEligibility {
  enabled: boolean;
  hasRole: boolean;
  hasLocation: boolean;
  eligible: boolean;
}

export interface DutySessionSummary {
  active: boolean;
  id?: number;
  roleCode?: string;
  locationCode?: string;
  roleDisplayName?: string;
  locationDisplayName?: string;
  /** Multi-role: the full set of roles for this session (primary first). */
  roles?: { code: string; displayName: string }[];
  signedInAt?: string;
  medicalConfirmed?: boolean;
  shouldShowReminder?: boolean;
}

export interface FeatureFlags {
  dutySignin: boolean;
  timesheetApp: boolean;
  groupQuickMessage: boolean;
  // Phase 8 — First Aider Callout kill-switch (features.first_aider_callout).
  // Off hides the Apps-page tile; in-flight incidents + responses are
  // unaffected. Optional-read (?? true) everywhere so a stale /api/me across a
  // deploy still shows the tile rather than hiding it.
  firstAiderCallout: boolean;
  // Cross-service switch for the "Live Trains" app link. Sourced from the live
  // tracker's own DB (via /api/me), NOT hub_system_prefs. When the public tracker
  // page is turned off from the Live Tracker app, this goes false and the tile
  // hides. Optional so a stale /api/me leaves the tile visible (fail open).
  liveTrackerHome?: boolean;
}

export interface MeResponse {
  upn: string;
  role: "admin" | "user" | null;
  authMethod: "sso" | "pin";
  groups: string[];
  isAdmin: boolean;
  isUser: boolean;
  isSsoAdmin: boolean;
  isManager: boolean;
  isNewsAdmin: boolean;
  /** Phase 5d -- hub_users.is_ceo_update. With isNewsAdmin, gates the CEO Newsletter Creator + CEO routing. Both flags required; either alone is insufficient. */
  isCeoUpdate: boolean;
  isAlertsAdmin: boolean;

  // Phase 9 -- role flags now sourced from hub_users (migration 032).
  isHrAdmin: boolean;
  isDm: boolean;
  isMom: boolean;
  isEm: boolean;
  // Duty rota roles (migrations 043/068) — surfaced so the Duty Scheduler opens for any duty-role holder.
  isDc: boolean;
  isDf: boolean;
  isFirstAider: boolean;

  // Phase 11 -- content-editor role flags (migration 044). Used by the
  // Docs and Contacts apps to gate write affordances (Upload / Add /
  // Edit / Delete) when the caller isn't a full admin. Server-side
  // gates in api/src/resources/{docs,contacts}.ts enforce the same
  // rule; the SPA hides the buttons up-front so the user never hits
  // a 403.
  isDocsEditor: boolean;
  isContactEditor: boolean;

  // Documents onboarding (migration 080). Gates the editor's Onboarding section —
  // who may mark a document as an onboarding/consent document. Admins always hold
  // it. The Confirmations & Consents app itself is open to everyone.
  isOnboardingAdmin: boolean;

  // Phase 7 -- Weekly Sheet Enhanced Data View. Drives the gating of
  // customerName + notes on /api/weekly-sheet items, and the visibility
  // of the locked-padlock footnote chip on the Weekly Sheet page.
  // Default false; the server still authoritatively redacts the gated
  // fields so this flag is purely a hint for client-side UI affordances.
  isWeeklySheetEnhanced: boolean;

  // migration 074 -- Driving Licence Check DV Admin role. SPA shows the admin
  // desktop view + the admin tile when true. Driver self-service is gated by
  // apps.drivingLicenceCheck instead.
  isDvCheckAdmin: boolean;

  // migration 085 -- Long Service Awards admin role. SPA shows the LS Admin
  // tile + console when true; the recipient LS Travel Pass is gated by
  // apps.longService instead.
  isLongServiceAdmin: boolean;
  // Moorsline Editor (migration 094). Gates the editor console + tile; reading
  // the magazine is universal so no flag gates it.
  isMoorslineEditor: boolean;
  // NYMR Newsletter Publisher (migration 124). Gates the publisher console + tile;
  // reading is gated by the on-by-default apps.newsletterReader grant instead.
  isNewsletterPublisher: boolean;
  // Training & Competencies (migration 099). Gates the Training admin console + tile.
  isTrainingAdmin: boolean;
  // Incident Log Full Access (migration 101). SPA shows the New Incident / edit /
  // manage surfaces when true; read-only access is the apps.incidentLog grant.
  isIncidentAdmin: boolean;

  // Phase 9 -- per-app permission map.
  apps: AppPermissions;

  // Phase 9 -- lifecycle + visibility preferences.
  signOnStatus: "pending" | "active";
  showMobileToAllHubUsers: boolean;
  emergencyContactVisibleTo: number[];

  first: string;
  last: string;
  departments: string[];

  // hub_users record (extended in Slice D)
  hubUserId: number | null;
  /** CODE-406: caller's Entra OID, lower-cased. Null on PIN-only users
   *  with no Entra OID stored. The Issue Manager's "Assign me" people-
   *  action reads this to call POST /api/issues/:id/assign without a
   *  typeahead lookup. */
  entraObjectId: string | null;
  contact_id: number | null;
  line_manager_contact_id: number | null;
  /** Free-text fallback when the manager isn't in hub_contacts (migration 019 + Slice F). */
  line_manager_name: string | null;
  member_id_number: number | null;
  onboarded_at: string | null;
  onboarded_skipped: boolean;

  // Slice J -- per-user UI preferences (migration 025). null = use defaults.
  preferences: UserPreferences | null;

  // Phase 7 -- Quick Message inbox unread count, computed live per-request.
  // Drives the bottom-nav Home dot. The home page does its own
  // /api/me/quick-messages fetch for the banner/pill data itself.
  inbox: {
    quickMessages: { unread: number };
    // Phase 8 — count of the caller's active, not-yet-responded First Aider
    // callouts. Drives the red Home dot in the bottom nav (which wins over the
    // blue QM dot). Optional-chained at read sites for stale-payload safety.
    firstAiderCallouts: { active: number };
  };

  // Documents onboarding (migration 080). Per-request consent status driving the
  // orange home banner: how many onboarding docs the caller still has to action,
  // plus the rolling 14-day deadline (only "Start countdown on first login" docs
  // drive it). deadline/daysLeft are null when nothing is outstanding.
  onboarding: {
    unreadCount: number;
    readCount: number;
    rejectedCount: number;
    deadline: string | null;
    daysLeft: number | null;
  };

  // Phase 9 c14 -- system maintenance toggle. When active and !isAdmin,
  // the shell renders the holding page instead of routing the user.
  systemMaintenance: SystemMaintenanceState;

  // Sign-In System (Build 1) -- duty eligibility, active session summary, and
  // the admin feature kill-switches. The Home duty surface + Group QM are gated
  // on signIn.eligible && features.dutySignin. Optional-chained at every read
  // site so a stale /api/me across a deploy degrades closed rather than throwing.
  signIn: SignInEligibility;
  dutySession: DutySessionSummary;
  features: FeatureFlags;

  // Issue Manager / Phase 3 (ENG-302). Per-module access + grant block.
  //   access:  user can reach the Issue Manager at all (replaces the loose
  //            `apps.issues !== false` gate; for now they coincide).
  //   admin:   member of the Issue Manager Admins Entra group. Bypasses
  //            all in-app group scoping; can manage permissions.
  //   manager: member of the Issue Manager Managers Entra group (admins
  //            are implicitly managers). Eligible to hold grants.
  //   grants:  per-group view/manage rows from issue.GroupPermission.
  //            Empty for non-managers; synthetic "all 12 groups at
  //            manage" for admins; real grants for managers.
  // The Issue Manager SPA reads this block to gate the management
  // sub-nav, hide lifecycle actions, default queue scope, and surface
  // the admin permissions page.
  issues: IssuesAccessBlock;
}

export interface IssuesAccessBlock {
  access: boolean;
  admin: boolean;
  manager: boolean;
  grants: IssueGrant[];
}

export interface IssueGrant {
  groupId: number;
  groupName: string;
  groupShortCode: string;
  level: "view" | "manage";
}

export type ThemeKey = "warm-gold" | "nymr-website";
export type LogoKey = "art-deco-train" | "nymr-logo";

// Phase 9 / Cut 2 -- calendar tweak persistence. Shape mirrored from
// api/src/auth.ts's CalendarPreferences; the two definitions must stay
// in lockstep because both sides JSON-parse the same column.
export type CalendarDefaultView = "list" | "month";

export interface CalendarPreferences {
  defaultView?: CalendarDefaultView;
  showPast?: boolean;
}

// Notifications Do-Not-Disturb (quiet hours). from/to are "HH:MM" 24h strings in
// local time (Europe/London). Mirrors api/src/auth.ts; the dispatcher suppresses
// email + SMS in the window (safety/urgent events + in-app signals exempt).
export interface DndPreferences {
  enabled: boolean;
  from: string;
  to: string;
}

export interface UserPreferences {
  theme?: ThemeKey;
  logo?: LogoKey;
  calendar?: CalendarPreferences;
  dnd?: DndPreferences;
}

const AUTH_HINT_KEY = "nymr_auth_hint"; // "sso" | "pin" — written when user last authed

function readAuthHint(): "sso" | "pin" | null {
  const v = localStorage.getItem(AUTH_HINT_KEY);
  return v === "sso" || v === "pin" ? v : null;
}

export function writeAuthHint(hint: "sso" | "pin") {
  localStorage.setItem(AUTH_HINT_KEY, hint);
}

export function clearAuthHint() {
  localStorage.removeItem(AUTH_HINT_KEY);
}

// Routes that are themselves the destination of redirectToLogin -- if a
// background /api/me call from inside one of these pages returns 401
// (which is the expected steady state on /login pre-sign-in), we must NOT
// redirect again or we'd loop on ourselves. useTheme() lives at the App
// level and calls useCurrentUser on every route including these public
// ones, so the loop is unavoidable without this guard.
const PUBLIC_AUTH_PATHS: ReadonlySet<string> = new Set([
  "/login",
  "/register",
  "/forgot-pin",
]);

function redirectToLogin() {
  if (PUBLIC_AUTH_PATHS.has(window.location.pathname)) return;
  const hint = readAuthHint();
  const returnTo = window.location.pathname + window.location.search;
  if (hint === "sso") {
    // Express path for returning SSO users -- bounce straight to Entra. If
    // the browser still has an active token they auto-login; if not, Entra
    // shows its own sign-in page. Either way, no detour through /login.
    window.location.replace(`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`);
  } else {
    // Everyone else -- PIN users (hint==="pin"), first-time visitors (no
    // hint), post-logout (hint cleared), or new device -- lands on the
    // /login choice page so they pick PIN vs SSO themselves.
    window.location.replace(`/login?return=${encodeURIComponent(returnTo)}`);
  }
}

export function useCurrentUser() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(async () => {
    const res = await fetch("/api/me", { credentials: "include" });
    if (res.status === 401) {
      redirectToLogin();
      return null;
    }
    if (!res.ok) throw new Error(`/api/me returned ${res.status}`);
    return (await res.json()) as MeResponse;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMe();
        if (cancelled || !data) return;
        writeAuthHint(data.authMethod);
        setMe(data);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  // refresh() lets callers re-fetch after a write that changes server state
  // (e.g. PATCH /api/me/line-manager). Doesn't toggle `loading` — the page
  // already has rendered content.
  const refresh = useCallback(async () => {
    try {
      const data = await fetchMe();
      if (data) {
        writeAuthHint(data.authMethod);
        setMe(data);
      }
    } catch (e) {
      setError(String(e));
    }
  }, [fetchMe]);

  return { me, loading, error, refresh };
}

// useLogout — Slice D wires the PIN-side server logout that was a TODO in
// Slice B. SSO callers redirect via SWA's /.auth/logout (browser handles
// the OAuth wind-down). PIN callers POST /api/auth/logout so the server
// invalidates the session_token, then we clear local hints and navigate
// to /login.
export function useLogout() {
  return async () => {
    const hint = readAuthHint();
    if (hint === "pin") {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // Server may be unreachable — the cookie clear and local navigate
        // are still useful. A stale session_token in the DB is harmless
        // (it just won't authenticate anyone since the client cookie is gone).
      }
      clearAuthHint();
      localStorage.removeItem("nymr_pin_email");
      window.location.replace("/login");
    } else {
      clearAuthHint();
      window.location.replace("/.auth/logout");
    }
  };
}
